import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { Input, Spinner } from '@vnetwork/ui/primitives';
import {
  useServices,
  serviceLabel,
  serviceScope,
  type AvailableService,
} from '@/api/services';
import { useDebounce } from '@/pages/services/shared';
import styles from './ServiceSelect.module.css';

interface ServiceSelectProps {
  selected: AvailableService | null;
  onSelect: (service: AvailableService) => void;
  disabled?: boolean;
  /** Only umbrella/orchestrator services (hides CDN per-provider children). Default true. */
  topLevel?: boolean;
  /** Extra client-side predicate on top of the server search (e.g. same-provider CDN only). */
  filter?: (service: AvailableService) => boolean;
  placeholder?: string;
}

/**
 * Searchable service picker. The catalog is large and paginated, so typing
 * queries the backend (`/services?search=`); results are also filtered
 * client-side as a fallback. The results panel renders in-flow (not in a
 * portal) so its list scrolls inside the dialog's scroll-lock.
 */
export function ServiceSelect({
  selected,
  onSelect,
  disabled,
  topLevel = true,
  filter,
  placeholder = 'Select a service',
}: ServiceSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const debounced = useDebounce(term, 250);

  const { data, isFetching } = useServices({ search: debounced, perPage: 50, topLevel });

  const results = useMemo(() => {
    const base = filter ? (data?.services ?? []).filter(filter) : data?.services ?? [];
    const q = debounced.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (s) =>
        serviceLabel(s).toLowerCase().includes(q) ||
        (s.provider?.display_name ?? '').toLowerCase().includes(q) ||
        (s.provider?.name ?? '').toLowerCase().includes(q),
    );
  }, [data, debounced, filter]);

  const total = data?.total ?? 0;

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Focus the search box when the panel opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function close() {
    setOpen(false);
    setTerm('');
  }

  function pick(s: AvailableService) {
    onSelect(s);
    close();
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        className={styles.trigger}
        data-placeholder={selected ? undefined : ''}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.triggerLabel}>
          {selected ? serviceLabel(selected) : placeholder}
        </span>
        <ChevronDown size={16} className={styles.chevron} />
      </button>

      {open && (
        <div className={styles.panel}>
          <Input
            ref={inputRef}
            placeholder="Search services…"
            startSlot={<Search />}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <div className={styles.list}>
            {results.length === 0 ? (
              <div className={styles.empty}>
                {isFetching ? <Spinner size="sm" /> : 'No services found.'}
              </div>
            ) : (
              results.map((s) => {
                const isSelected = selected?.id === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pick(s)}
                    className={isSelected ? `${styles.item} ${styles.itemSelected}` : styles.item}
                  >
                    <Check
                      size={14}
                      className={isSelected ? styles.check : `${styles.check} ${styles.checkHidden}`}
                    />
                    <span className={styles.itemText}>
                      <span className={styles.itemTitle}>{serviceLabel(s)}</span>
                      <span className={styles.itemScope}>{serviceScope(s)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {total > results.length && (
            <div className={styles.footer}>
              Showing {results.length} of {total}. Keep typing to narrow results.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
